import React, {
  useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { getChipsLabel } from '../../utils/search';
import Icon from '../Icons/Icon';
import ScrollButton from '../ScrollButton/ScrollButton';

const DEFAULT_CHIPS = [];

const SearchChipsCarousel = ({
  chips = DEFAULT_CHIPS,
  onClickCloseChip = () => {},
  chipsSettings = {},
  isAdvancedSearch = false,
}) => {
  const containerRef = useRef(null);
  const [showArrows, setShowArrows] = useState(false);
  const [isDisabledLeftButton, setIsDisabledLeftButton] = useState(true);
  const [isDisabledRightButton, setIsDisabledRightButton] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const isOverflowing = containerRef.current.scrollWidth > containerRef.current.clientWidth;
    setShowArrows(isOverflowing);
  }, [chips, containerRef]);

  // handle scroll buttons if the containerRef is overflowing
  useEffect(() => {
    const container = containerRef.current;
    // handle the scroll event disable the arrow buttons
    const handleClick = () => {
      setIsDisabledLeftButton(container.scrollLeft === 0);
      // we need to subtract 1px in the scrollLeft to make sure the scroll is at the end, because the scrollLeft is not always the same as the scrollWidth-scrollLeft
      setIsDisabledRightButton(container.scrollLeft >= (container.scrollWidth - container.clientWidth - 1));
    };

    // all below this lane will happen if container is overloading
    // add event listener to the scroll event if the container is present and is overflowing
    if (container && showArrows) {
      container.addEventListener('scroll', handleClick);
    }
    return () => {
      if (container && showArrows) {
        container.removeEventListener('scroll', handleClick);
        setIsDisabledLeftButton(true);
        setIsDisabledRightButton(false);
      }
    };
  }, [showArrows, containerRef]);

  return (
    <>
      {showArrows && (
      <ScrollButton
        onClick={() => containerRef.current?.scrollBy({ left: -containerRef?.current.clientWidth, behavior: 'smooth' })}
        direction="left"
        isDisabled={isDisabledLeftButton}
      />
      )}
      <div ref={containerRef} className="flex-1 w-full overflow-x-auto scrollbar-hidden">
        <div className="flex-1 inline-flex w-full gap-x-1.5 whitespace-nowrap">
          {chips && chips.map((chip) => {
            const chipLabel = isAdvancedSearch ? chip.value : getChipsLabel(chipsSettings, chip.key, chip.value);
            return (
              <span key={chip.key} className="px-2 py-1 rounded-xl inline-flex gap-2 bg-vamos-v-light-gray text-vamos-gray text-sm">
                {chipLabel}
                <button
                  type="button"
                  aria-label="close chip"
                  className="flex items-center justify-center self-center ml-2 mr-4 w-4.5 h-4.5"
                  onClick={(e) => onClickCloseChip(e, chip.key)}
                  onKeyDown={(e) => e.key === 'Enter' && onClickCloseChip(e, chip.key)}
                >
                  <Icon name="close_small" className="w-1.75 h-1.75 text-vamos-gray" />
                </button>
              </span>
            );
          })}
        </div>
      </div>
      {showArrows && (
      <ScrollButton
        onClick={() => containerRef.current?.scrollBy({ left: containerRef?.current.clientWidth, behavior: 'smooth' })}
        direction="right"
        isDisabled={isDisabledRightButton}
      />
      )}
    </>
  );
};

SearchChipsCarousel.propTypes = {
  chips: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    value: PropTypes.string,
  })),
  onClickCloseChip: PropTypes.func,
  chipsSettings: PropTypes.object,
  isAdvancedSearch: PropTypes.bool,
};

export default SearchChipsCarousel;
